import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';
import { SEOHead } from '@/components/atoms/SEOHead';
import { BoardTabs } from '@/components/organisms/BoardTabs';
import { sanitizePostHtml } from '@/lib/sanitize';
import { colorizePercent } from '@/lib/colorize';
import { MARKET_REPORT_CATEGORIES, type MarketReportCategoryKey } from '@/constants/boards';
import { UsCloseChart } from '@/components/charts/UsCloseChart';
import { CoinChart } from '@/components/charts/CoinChart';
import { KrCloseChart } from '@/components/charts/KrCloseChart';
import { MorningChart } from '@/components/charts/MorningChart';
import { KrMorningChart } from '@/components/charts/KrMorningChart';
import { timeAgo } from '@/lib/format';

/** KST 기준 yyyy-mm-dd 추출 — DB DATE 컬럼은 자정 UTC 로 직렬화돼 -9h 표시되는 문제 회피.
 *  Date 객체에 9시간 더한 후 toISOString().slice(0,10) 으로 KST 날짜 부분만 사용. */
function fmtDateKST(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
}

/** KST 기준 yyyy-mm-dd HH:MM (작성 시각 표기용) */
function fmtDateTimeKST(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const s = kst.toISOString();
  return `${s.slice(0, 10)} ${s.slice(11, 16)} KST`;
}

interface ReportDetail {
  id: number;
  category: MarketReportCategoryKey;
  title: string;
  summary: string | null;
  /** 본문 — market_reports.content (migration 046 이후 posts 의존 제거) */
  content: string | null;
  report_date: string;
  created_at: string;
  /** legacy — 더 이상 사용 안 함 (NULL 로 들어옴) */
  post_id: number | null;
  /** meta.ai — morning_brief 헤더 brief + 섹션별 AI 분석 텍스트.
   *  - brief: 미장/국장/BTC 3-key 요약 (헤더 article)
   *  - fx_analysis: 원/달러 환율 분석 (차트 위)
   *  - us_market_summary: 미국주식 시장총평 (차트 위)
   *  - 기타 섹션별 텍스트 */
  meta?: {
    ai?: {
      brief?: {
        us?:   { intro?: string; metrics?: string };
        kr?:   { intro?: string; metrics?: string };
        coin?: { intro?: string; metrics?: string; fng?: string };
      };
      fx_analysis?: string;
      us_market_summary?: string;
      us_movers?: string;
      us_fed_policy?: string;
      kr_market_summary?: string;
      coin_summary?: string;
      [key: string]: unknown;
    };
    charts?: Record<string, unknown>;
  } | null;
}

interface ChartData {
  category: MarketReportCategoryKey;
  charts: Record<string, unknown>;
}

/** 시장상황 게시판 상세 페이지 — 본문 상단 차트 + 하단 텍스트 본문.
 *  카드 클릭 시 /market/:id 로 진입 (기존 /post/:id 와 별도 경로). */
export function MarketReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const reportId = Number(id);

  const detailQ = useQuery({
    queryKey: ['market-reports', reportId],
    queryFn: () => get<{ report: ReportDetail }>(`/market-reports/${reportId}`),
    enabled: Number.isFinite(reportId),
    retry: (count, err) => {
      const status = (err as any)?.response?.status;
      if (status === 404) return false;
      return count < 2;
    },
  });

  /* 잘못된 id (NaN) 또는 404 — NotFoundPage 렌더링 (아래 분기). */
  const isReportNotFound =
    !Number.isFinite(reportId) ||
    (detailQ.error as any)?.response?.status === 404;

  const chartQ = useQuery({
    queryKey: ['market-reports', reportId, 'chart-data'],
    queryFn: () => get<ChartData>(`/market-reports/${reportId}/chart-data`),
    enabled: Number.isFinite(reportId),
    staleTime: 5 * 60_000,
  });

  if (isReportNotFound) {
    return <NotFoundPage />;
  }

  if (detailQ.isLoading) {
    return (
      <>
        <BoardTabs activeSlug="market" />
        <div className="container-base pb-6">
          <Skeleton className="mb-4 h-9 w-1/2" />
          <Skeleton className="h-72" />
        </div>
      </>
    );
  }

  const r = detailQ.data?.report;
  if (!r) return null;
  const catMeta = MARKET_REPORT_CATEGORIES.find((c) => c.key === r.category);

  // 모닝브리핑은 섹션별 article 분리 (요약 1 + 본문 섹션 N).
  if (r.category === 'morning_brief') {
    return (
      <MorningBriefView
        r={r}
        chartData={chartQ.data}
        chartLoading={chartQ.isLoading}
      />
    );
  }

  // 카테고리별 타이틀 통일 — '{MM/DD} XXX 브리핑' 형태.
  // 코인 24H / 국장 마감 / 장중은 카테고리 태그 + 작성시각 footer 모두 숨김.
  // 미장 마감은 footer 작성시각만 숨김 (헤더 메타는 유지).
  const isCoin24h = r.category === 'coin_24h';
  const isKrClose = r.category === 'kr_close';
  const isIntraday = r.category === 'intraday';
  const isUsClose = r.category === 'us_close';
  const hideHeaderMeta = isCoin24h || isKrClose || isIntraday;
  const hideCreatedFooter = hideHeaderMeta || isUsClose;
  const titleText = isCoin24h
    ? `${fmtDateMD(r.report_date)} 코인 24H 브리핑`
    : isKrClose
    ? `${fmtDateMD(r.report_date)} 국장마감 브리핑`
    : isIntraday
    ? `${fmtDateMD(r.report_date)} 장중 브리핑`
    : r.title;

  // coin_24h 본문 최상단의 시간 범위 라인을 추출해 h1 옆 인라인으로 노출.
  const { range: coinPeriodRange, html: contentWithoutPeriod } = isCoin24h
    ? extractPeriodRange(r.content || '')
    : { range: null, html: r.content || '' };

  return (
    <>
      <SEOHead title={`${r.title} — 시세공간`} description={r.summary ?? ''} />
      <BoardTabs activeSlug="market" />
      <div className="container-base pb-6">
        <article className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-6">
          {!hideHeaderMeta && (
            <div className="mb-2 flex items-center gap-2">
              <Badge variant="primary">{catMeta?.label ?? r.category}</Badge>
              <span className="text-xs text-[var(--color-text-sub)]">{fmtDateKST(r.report_date)}</span>
            </div>
          )}
          <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl font-extrabold leading-snug">
              {hideHeaderMeta ? titleText : colorizePercent(titleText)}
            </h1>
            {coinPeriodRange && (
              <span className="text-xs font-normal text-[var(--color-text-sub)]">
                {coinPeriodRange}
              </span>
            )}
          </div>

          {/* 국장 마감 / 장중 브리핑 — 모닝브리핑 국내주식 섹션과 동일한 폼:
                ① <h3>시장총평</h3> + 텍스트(summary)
                ② <h3>주요 지수</h3> + KrCloseChart
                ③ (국장 마감만) 차트 직하 index-line (KOSPI/KOSDAQ 종가·등락률) */}
          {isKrClose || isIntraday ? (
            <div className="mb-6 space-y-5">
              {r.summary && (
                <div>
                  <h3 className="mb-2 text-base font-bold">시장총평</h3>
                  <p
                    className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text)] post-content"
                    dangerouslySetInnerHTML={{ __html: sanitizePostHtml(r.summary) }}
                  />
                </div>
              )}
              <div>
                <h3 className="mb-2 text-base font-bold">주요 지수</h3>
                {chartQ.isLoading ? (
                  <Skeleton className="h-64" />
                ) : chartQ.data ? (
                  <CategoryCharts data={chartQ.data} />
                ) : null}
                {(isKrClose || isIntraday) && chartQ.data && (
                  <KrCloseIndexLines charts={(chartQ.data.charts as Record<string, unknown>) ?? {}} />
                )}
              </div>
            </div>
          ) : (
            <>
              {r.summary && (
                <p
                  className="mb-6 text-sm text-[var(--color-text)] post-content"
                  dangerouslySetInnerHTML={{ __html: sanitizePostHtml(r.summary) }}
                />
              )}
              <div className="mb-6">
                {chartQ.isLoading ? (
                  <Skeleton className="h-64" />
                ) : chartQ.data ? (
                  <CategoryCharts data={chartQ.data} />
                ) : null}
              </div>
            </>
          )}

          {/* 본문 텍스트 — 차트 하단. coin_24h 는 period-range 라인을 분리해 h1 옆 노출했으므로 제외된 HTML 사용 */}
          {(isCoin24h ? contentWithoutPeriod : r.content) && (
            <div
              className="prose prose-sm max-w-none post-content"
              dangerouslySetInnerHTML={{
                __html: sanitizePostHtml(isCoin24h ? contentWithoutPeriod : (r.content || '')),
              }}
            />
          )}

          {/* 작성시각 footer — 코인 24H / 국장 마감 / 장중 / 미장 마감은 노출 안 함 */}
          {!hideCreatedFooter && (
            <div className="mt-8 border-t border-[var(--color-border)] pt-4 text-xs text-[var(--color-text-sub)]">
              <span>
                {fmtDateTimeKST(r.created_at)}
                <span className="ml-2 text-[var(--color-text-sub)]/60">
                  ({timeAgo(r.created_at)})
                </span>
              </span>
            </div>
          )}
        </article>
      </div>
    </>
  );
}

/** 국장 마감 차트 아래 KOSPI/KOSDAQ 종가·등락률 라인.
 *  분봉 차트 전환 후, 마감 시세는 chart-data 응답의 별도 필드 `kospiClose`/`kosdaqClose` (일봉 마지막 행) 사용.
 *  모닝브리핑 본문의 `p.index-line` 마크업/스타일과 동일 (post-content 컨텍스트 필수). */
function KrCloseIndexLines({ charts }: { charts: Record<string, unknown> }) {
  const pick = (key: 'kospiClose' | 'kosdaqClose') => {
    const v = charts[key];
    if (!v || typeof v !== 'object') return null;
    const close = (v as { close?: number }).close;
    const rate  = (v as { change_rate?: number }).change_rate;
    if (typeof close !== 'number') return null;
    return { close, change_rate: typeof rate === 'number' ? rate : 0 };
  };
  const kospi = pick('kospiClose');
  const kosdaq = pick('kosdaqClose');
  if (!kospi && !kosdaq) return null;
  const lines: Array<{ label: string; close: number; rate: number }> = [];
  if (kospi) lines.push({ label: 'KOSPI', close: kospi.close, rate: kospi.change_rate });
  if (kosdaq) lines.push({ label: 'KOSDAQ', close: kosdaq.close, rate: kosdaq.change_rate });
  return (
    <div className="post-content mt-4">
      {lines.map((l) => {
        const sign = l.rate > 0 ? '+' : '';
        const pctClass = l.rate > 0 ? 'up' : l.rate < 0 ? 'down' : '';
        return (
          <p key={l.label} className="index-line text-sm">
            {l.label} <strong>{l.close.toFixed(2)}</strong>{' '}
            <span className={pctClass}>({sign}{l.rate.toFixed(2)}%)</span>
          </p>
        );
      })}
    </div>
  );
}

/** KST yyyy-mm-dd → "MM/DD" 짧은 표기 */
function fmtDateMD(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return `${(kst.getUTCMonth() + 1).toString().padStart(2, '0')}/${kst.getUTCDate().toString().padStart(2, '0')}`;
}

/** 헤더 3-key 요약 — 미장 / 국장 / BTC 각 라벨 + intro + metrics 줄바꿈 렌더.
 *  brief 누락 시 fallbackSummary (평문) 폴백.
 *  코인은 metrics(BTC 가격/등락) + fng(공포탐욕 한글) 둘 다 별도 라인. */
function BriefHeader({
  brief,
  fallbackSummary,
}: {
  brief:
    | {
        us?: { intro?: string; metrics?: string };
        kr?: { intro?: string; metrics?: string };
        coin?: { intro?: string; metrics?: string; fng?: string };
      }
    | undefined;
  fallbackSummary: string | null;
}) {
  type Section = { label: string; intro?: string; lines: string[] };
  const sections: Section[] = [];
  // metrics 는 '\n' 으로 구분된 멀티라인 — 각 지수를 별도 <p> 로 렌더.
  const splitLines = (s: string | undefined) =>
    (s ?? '').split('\n').map((l) => l.trim()).filter(Boolean);

  if (brief?.us && (brief.us.intro || brief.us.metrics)) {
    sections.push({
      label: '미장',
      intro: brief.us.intro,
      lines: splitLines(brief.us.metrics),
    });
  }
  if (brief?.kr && (brief.kr.intro || brief.kr.metrics)) {
    sections.push({
      label: '국장',
      intro: brief.kr.intro,
      lines: splitLines(brief.kr.metrics),
    });
  }
  if (brief?.coin && (brief.coin.intro || brief.coin.metrics || brief.coin.fng)) {
    sections.push({
      label: 'BTC',
      intro: brief.coin.intro,
      lines: [...splitLines(brief.coin.metrics), ...splitLines(brief.coin.fng)],
    });
  }

  if (sections.length === 0) {
    if (!fallbackSummary) return null;
    return (
      <p
        className="text-sm leading-relaxed text-[var(--color-text)] post-content"
        dangerouslySetInnerHTML={{ __html: sanitizePostHtml(fallbackSummary) }}
      />
    );
  }

  return (
    <div className="space-y-5">
      {sections.map((sec) => (
        <div key={sec.label}>
          <div className="mb-1 text-base font-bold">{sec.label}</div>
          {sec.intro && (
            <p className="text-sm leading-relaxed text-[var(--color-text)]">
              {sec.intro}
            </p>
          )}
          {sec.lines.map((line, i) => (
            <p
              key={i}
              className="text-sm leading-relaxed text-[var(--color-text)] post-content"
              dangerouslySetInnerHTML={{ __html: sanitizePostHtml(line) }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** content HTML 을 <h2> 기준으로 섹션 단위로 split.
 *  각 섹션: { title, html } — html 은 해당 <h2> 다음 형제 노드부터 다음 <h2> 전까지. */
/**
 * content/html 의 첫 <p class="period-range">…</p> 를 추출.
 * coin_24h 본문 최상단 / morning_brief 코인 섹션 헤더 직후의 시간 범위 라인을
 * h1/h2 우측 인라인 렌더로 옮기기 위해 사용.
 */
function extractPeriodRange(html: string): { range: string | null; html: string } {
  const m = html.match(/<p\s+class="period-range">([\s\S]*?)<\/p>\s*/);
  if (!m) return { range: null, html };
  return { range: m[1].trim(), html: html.replace(m[0], '') };
}

function splitContentBySections(html: string): { title: string; html: string }[] {
  if (typeof DOMParser === 'undefined') return [];
  const doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  if (!root) return [];
  const out: { title: string; html: string }[] = [];
  let cur: { title: string; chunks: string[] } | null = null;
  for (const node of Array.from(root.childNodes)) {
    const isH2 = node.nodeType === 1 && (node as Element).tagName === 'H2';
    if (isH2) {
      if (cur) out.push({ title: cur.title, html: cur.chunks.join('') });
      cur = { title: (node as Element).textContent || '', chunks: [] };
      continue;
    }
    if (!cur) continue;
    if (node.nodeType === 1) cur.chunks.push((node as Element).outerHTML);
    else if (node.nodeType === 3) cur.chunks.push(node.textContent || '');
  }
  if (cur) out.push({ title: cur.title, html: cur.chunks.join('') });
  return out;
}

interface MorningBriefViewProps {
  r: ReportDetail;
  chartData: ChartData | undefined;
  chartLoading: boolean;
}

/** 모닝브리핑 전용 뷰 — 요약 article + 섹션별 article.
 *  섹션별 차트 매핑: 원/달러→MorningChart, 미국주식→UsCloseChart, 코인→CoinChart. */
function MorningBriefView({ r, chartData, chartLoading }: MorningBriefViewProps) {
  const sections = splitContentBySections(r.content || '').map((s) => {
    const { range, html } = extractPeriodRange(s.html);
    return { title: s.title, html, periodRange: range };
  });
  const c = (chartData?.charts ?? {}) as Record<string, any>;

  const renderChartForSection = (title: string) => {
    if (chartLoading) return <Skeleton className="mb-4 h-64" />;
    if (!chartData) return null;
    if (title === '원/달러 환율') {
      const fxAnalysis: string = c.fxAnalysis ?? '';
      return (
        <div className="mb-4 space-y-5">
          {/* 타이틀 바로 밑 — AI 분석 텍스트 */}
          {fxAnalysis && (
            <div>
              <h3 className="mb-2 text-base font-bold">원/달러 환율 분석</h3>
              <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text)]">
                {fxAnalysis}
              </p>
            </div>
          )}
          {/* 차트 — 분석 아래 */}
          <MorningChart usdkrw={c.usdkrw ?? []} />
        </div>
      );
    }
    if (title === '미국주식') {
      const usMarket: string = r.meta?.ai?.us_market_summary ?? '';
      return (
        <div className="mb-4 space-y-5">
          {/* 타이틀 바로 밑 — 시장총평 (차트 위) */}
          {usMarket && (
            <div>
              <h3 className="mb-2 text-base font-bold">시장총평</h3>
              <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text)]">
                {usMarket}
              </p>
            </div>
          )}
          {/* 주요 지수 — S&P/NASDAQ/Dow 3분할 차트 */}
          <div>
            <h3 className="mb-2 text-base font-bold">주요 지수</h3>
            <UsCloseChart sp500={c.sp500 ?? []} nasdaq={c.nasdaq ?? []} dow={c.dow ?? []} />
          </div>
        </div>
      );
    }
    if (title === '국내주식') {
      const krMarket: string = r.meta?.ai?.kr_market_summary ?? '';
      return (
        <div className="mb-4 space-y-5">
          {/* 타이틀 바로 밑 — 시장총평 (차트 위) */}
          {krMarket && (
            <div>
              <h3 className="mb-2 text-base font-bold">시장총평</h3>
              <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text)]">
                {krMarket}
              </p>
            </div>
          )}
          {/* 주요 지수 — KOSPI/KOSDAQ 2분할 차트 */}
          <div>
            <h3 className="mb-2 text-base font-bold">주요 지수</h3>
            <KrMorningChart kospi={c.kospi ?? []} kosdaq={c.kosdaq ?? []} />
          </div>
        </div>
      );
    }
    if (title === '코인') {
      const coinSummary: string = r.meta?.ai?.coin_summary ?? '';
      return (
        <div className="mb-4 space-y-5">
          {/* 타이틀 바로 밑 — 시장총평 (차트 위) */}
          {coinSummary && (
            <div>
              <h3 className="mb-2 text-base font-bold">시장총평</h3>
              <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text)]">
                {coinSummary}
              </p>
            </div>
          )}
          {/* 주요 시세 — BTC/ETH 차트 (지수값은 본문 index-line 으로 차트 아래 노출) */}
          <div>
            <h3 className="mb-2 text-base font-bold">주요 시세</h3>
            <CoinChart btc={c.btc ?? []} eth={c.eth ?? []} fearGreed={c.fearGreed ?? null} />
          </div>
        </div>
      );
    }
    return null;
  };

  const articleClass = 'rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 sm:p-6';

  return (
    <>
      <SEOHead title={`${r.title} — 시세공간`} description={r.summary ?? ''} />
      <BoardTabs activeSlug="market" />
      <div className="container-base pb-6 space-y-4">
        {/* 1. 요약 article — 카테고리 태그/작성시각 footer 없이 타이틀만 표기 */}
        <article className={articleClass}>
          <h1 className="mb-5 text-2xl font-extrabold leading-snug">
            {fmtDateMD(r.report_date)} 모닝브리핑
          </h1>
          <BriefHeader brief={r.meta?.ai?.brief} fallbackSummary={r.summary} />
        </article>

        {/* 2~N. 섹션별 article */}
        {sections.map((sec) => (
          <article key={sec.title} className={articleClass}>
            <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="text-xl font-bold leading-snug">{sec.title}</h2>
              {sec.periodRange && (
                <span className="text-xs font-normal text-[var(--color-text-sub)]">
                  {sec.periodRange}
                </span>
              )}
            </div>
            {renderChartForSection(sec.title)}
            <div
              className="prose prose-sm max-w-none post-content"
              dangerouslySetInnerHTML={{ __html: sanitizePostHtml(sec.html) }}
            />
          </article>
        ))}
      </div>
    </>
  );
}

/** 카테고리별 차트 컴포넌트 매핑 */
function CategoryCharts({ data }: { data: ChartData }) {
  const c = data.charts as Record<string, never>;
  switch (data.category) {
    case 'intraday':
      // 장중 브리핑 — KOSPI/KOSDAQ 5분봉 + X축 09:00~15:00 KST.
      return (
        <KrCloseChart
          kospi={(c as any).kospi ?? []}
          kosdaq={(c as any).kosdaq ?? []}
          endTime="15:00"
        />
      );
    case 'kr_close':
      // 국장 마감 — 당일 5분봉 + X축 09:00~15:30 KST.
      return (
        <KrCloseChart
          kospi={(c as any).kospi ?? []}
          kosdaq={(c as any).kosdaq ?? []}
          endTime="15:30"
        />
      );
    case 'us_close':
      return (
        <UsCloseChart
          sp500={(c as any).sp500 ?? []}
          nasdaq={(c as any).nasdaq ?? []}
          dow={(c as any).dow ?? []}
        />
      );
    case 'coin_24h':
      return (
        <CoinChart
          btc={(c as any).btc ?? []}
          eth={(c as any).eth ?? []}
          fearGreed={(c as any).fearGreed ?? null}
        />
      );
    case 'morning_brief':
      return (
        <div className="space-y-6">
          <MorningChart
            usdkrw={(c as any).usdkrw ?? []}
            fxAnalysis={(c as any).fxAnalysis ?? ''}
          />
          <UsCloseChart
            sp500={(c as any).sp500 ?? []}
            nasdaq={(c as any).nasdaq ?? []}
            dow={(c as any).dow ?? []}
          />
          <CoinChart
            btc={(c as any).btc ?? []}
            eth={(c as any).eth ?? []}
            fearGreed={(c as any).fearGreed ?? null}
          />
        </div>
      );
    default:
      return null;
  }
}
